import {
  signInWithCustomToken,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
  IdTokenResult,
} from 'firebase/auth';
import { adminAuth, isFirebaseConfigured } from './config';

export async function signInWithToken(token: string) {
  if (!adminAuth) return { user: null, error: 'Firebase not configured' };
  try {
    const result = await signInWithCustomToken(adminAuth, token);
    return { user: result.user, error: null };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Sign in failed';
    return { user: null, error: message };
  }
}

export async function signOut() {
  if (!adminAuth) return { error: null };
  try {
    await firebaseSignOut(adminAuth);
    return { error: null };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Sign out failed';
    return { error: message };
  }
}

function waitForAuthReady(timeoutMs = 5000): Promise<User | null> {
  if (!adminAuth) return Promise.resolve(null);
  return new Promise((resolve) => {
    if (adminAuth!.currentUser) {
      resolve(adminAuth!.currentUser);
      return;
    }
    const timeout = setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, timeoutMs);
    const unsubscribe = onAuthStateChanged(adminAuth!, (user) => {
      clearTimeout(timeout);
      unsubscribe();
      resolve(user);
    });
  });
}

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  if (!adminAuth) return null;
  let user = adminAuth.currentUser;
  if (!user) {
    user = await waitForAuthReady();
  }
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export async function getTokenResult(): Promise<IdTokenResult | null> {
  if (!adminAuth) return null;
  const user = adminAuth.currentUser;
  if (!user) return null;
  return user.getIdTokenResult();
}

export async function isAdmin(): Promise<boolean> {
  const tokenResult = await getTokenResult();
  return tokenResult?.claims?.admin === true;
}

export function subscribeToAuthState(callback: (user: User | null) => void) {
  if (!adminAuth) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(adminAuth, callback);
}

export function getCurrentUser(): User | null {
  if (!adminAuth) return null;
  return adminAuth.currentUser;
}

export { isFirebaseConfigured };
export type { User, IdTokenResult };
